import { sendNotification } from "../utils/notifications";
import { storeNotification } from "../services/notificationService";
import { addNewDoc } from "./firebase/Firebase.utils";
import { adminEmailAddress } from "./constants";

const ADMIN_EMAIL = adminEmailAddress;

/**
 * Sends a notification to the admin for any user activity
 * @param {string} activityType - Type of activity (guestPass, gatePass, payment, etc.)
 * @param {string} userName - Name of the user who performed the action
 * @param {string} userUnit - Unit number of the user
 * @param {string} itemId - ID of the created/modified item
 * @param {Object} additionalData - Any additional data to include
 * @returns {Promise<boolean>} Success status
 */
export const notifyAdmin = async (
  activityType,
  userName,
  userUnit,
  itemId,
  additionalData = {}
) => {
  try {
    // Create appropriate notification based on activity type
    const notification = createAdminNotification(
      activityType,
      userName,
      userUnit,
      itemId,
      additionalData
    );

    // Store notification in Firestore first (this always works even if FCM fails)
    await storeNotification(ADMIN_EMAIL, notification);

    // Create a separate FCM notification with specific format
    const fcmNotification = {
      title: notification.subject,
      body: notification.message,
      data: {
        type: notification.type || activityType,
        itemId: itemId,
        url: getFCMUrlFromType(activityType),
        timestamp: new Date().toISOString(),
        // Mark as FCM notification to distinguish from regular notifications
        isFCM: true,
      },
    };

    // Try to send via FCM (may fail if token isn't available)
    await sendNotification(ADMIN_EMAIL, fcmNotification);

    return true;
  } catch (error) {
    console.error(
      `Error sending admin notification for ${activityType}:`,
      error
    );
    return false;
  }
};

// Helper function to determine URL for FCM notifications
const getFCMUrlFromType = (type) => {
  switch (type) {
    case "guestPass":
      return "/admin/guests";
    case "gatePass":
      return "/admin/gate-passes";
    case "concern":
      return "/admin/concerns";
    case "billPayment":
      return "/admin/billing";
    case "workPermit":
      return "/admin/work-permits";
    default:
      return "/admin/dashboard";
  }
};

/**
 * Creates a notification for admin when users perform actions
 * This function creates notifications for admin from user activities
 * and stores them in Firestore
 *
 * @param {string} action - The action performed (billPayment, concern, guestPass, gatePass, workPermit)
 * @param {object} data - Data related to the action
 * @param {object} user - User who performed the action
 * @returns {Promise} - The result of creating the notification
 */
export const createAdminNotification = async (action, data, user) => {
  // Default notification object
  let notification = {
    to: adminEmailAddress,
    from: user.email,
    dateTime: new Date(),
    read: false,
    userId: user.uid || "",
    userName: user.displayName || "",
    userUnit: user.unit || "",
  };

  // Customize notification based on action type
  switch (action) {
    case "billPayment":
      notification = {
        ...notification,
        subject: `Bill Payment: ${user.displayName || user.email}`,
        message: `${user.displayName || user.email} has made a payment of ${
          data.amount
        } for ${data.billType || "bill"}.`,
        type: "billPayment",
        billId: data.billId || "",
        amount: data.amount || "",
        billType: data.billType || "",
      };
      break;

    case "concern":
      notification = {
        ...notification,
        subject: `New Concern: ${user.displayName || user.email}`,
        message: `${
          user.displayName || user.email
        } has submitted a new concern: ${data.title || "No title"}.`,
        type: "concern",
        concernId: data.id || "",
        concernType: data.type || "",
        priority: data.priority || "normal",
      };
      break;

    case "guestPass":
      notification = {
        ...notification,
        subject: `Guest Pass: ${user.displayName || user.email}`,
        message: `${user.displayName || user.email} has registered a guest: ${
          data.guestName || "Unnamed guest"
        }.`,
        type: "guestPass",
        guestId: data.id || "",
        guestName: data.guestName || "",
        visitDate: data.visitDate || new Date(),
      };
      break;

    case "gatePass":
      notification = {
        ...notification,
        subject: `Gate Pass: ${user.displayName || user.email}`,
        message: `${
          user.displayName || user.email
        } has requested a gate pass for ${data.visitorName || "visitor"}.`,
        type: "gatePass",
        gatePassId: data.id || "",
        visitorName: data.visitorName || "",
        purpose: data.purpose || "",
        date: data.date || new Date(),
      };
      break;

    case "workPermit":
      notification = {
        ...notification,
        subject: `Work Permit: ${user.displayName || user.email}`,
        message: `${
          user.displayName || user.email
        } has requested a work permit for ${
          data.workDescription || "maintenance work"
        }.`,
        type: "workPermit",
        workPermitId: data.id || "",
        workDescription: data.workDescription || "",
        startDate: data.startDate || new Date(),
        endDate: data.endDate || new Date(),
      };
      break;

    default:
      notification = {
        ...notification,
        subject: `User Action: ${user.displayName || user.email}`,
        message: `${
          user.displayName || user.email
        } has performed an action: ${action}.`,
        type: action,
      };
  }

  try {
    // Add notification to Firestore
    const result = await addNewDoc("notifications", notification);
    return result;
  } catch (error) {
    console.error("Error creating admin notification:", error);
    return error;
  }
};
